// ARCHIVO COMPARTIDO app/web — editar en ambos repos. Verificar con `npm run check:shared` (TemControlApp).
//
// Helpers para resolver "la entidad vigente en una fecha" a partir de un
// histórico relacional del backend (PersonAssignment / ScheduleVersion).
//
// Cada entrada del histórico tiene la forma:
//   { id, validFrom: 'YYYY-MM-DD', validTo: 'YYYY-MM-DD' | null, ... }
// El backend cierra automáticamente la entrada anterior cuando se crea
// una nueva, así que solo UNA entrada cumple `from <= date <= to` para
// cualquier fecha dada.
//
// Estos helpers se usan para que el cómputo de la bolsa de horas y el
// conteo de vacaciones respeten los cambios históricos del trabajador:
// si pasó de 38h/sem a 40h/sem el 1-jul, los días anteriores a esa fecha
// se cuentan con el horario antiguo y los posteriores con el nuevo.
//
// ZONA HORARIA: la fecha que se consulta puede ser una clave 'YYYY-MM-DD'
// (lo normal) o un instante (Date / timestamp). Un instante se convierte al
// día en la zona del TRABAJADOR (`timeZone`, por defecto Europe/Madrid —
// NUNCA la del dispositivo). El día de la semana se calcula sobre la clave
// de día de forma pura (src/lib/workerTime.js).

import { DEFAULT_TIME_ZONE, dayKeyInZone, dowOfDayKey } from './workerTime';

// Strings → sus 10 primeros caracteres ('YYYY-MM-DD', también para validFrom
// / validTo que el backend devuelva con hora). Date / timestamp → día en la
// zona del trabajador.
const isoOf = (date, timeZone = DEFAULT_TIME_ZONE) => {
  if (date == null) return null;
  if (typeof date === 'string') return date.slice(0, 10);
  return dayKeyInZone(date, timeZone);
};

// Busca en el histórico la entrada vigente en la fecha dada.
// Comparación lexicográfica directa sobre strings 'YYYY-MM-DD' (válida
// porque el formato es ordenable). validFrom inclusivo, validTo inclusivo.
// Devuelve null si nada cubre esa fecha.
export const findActiveEntry = (history, date, timeZone = DEFAULT_TIME_ZONE) => {
  const iso = isoOf(date, timeZone);
  if (!iso || !Array.isArray(history)) return null;
  for (const entry of history) {
    const from = isoOf(entry?.validFrom, timeZone);
    const to = isoOf(entry?.validTo, timeZone);
    if (!from) continue;
    if (from > iso) continue;
    if (to && to < iso) continue;
    return entry;
  }
  return null;
};

// Devuelve los slots (array de hasta 7 elementos) vigentes en una fecha
// del scheduleHistory. null si la versión vigente no tiene assignSlots o
// si no hay versión cubriendo esa fecha.
export const slotsAtDate = (scheduleHistory, date, timeZone = DEFAULT_TIME_ZONE) => {
  const entry = findActiveEntry(scheduleHistory, date, timeZone);
  if (!entry || !Array.isArray(entry.assignSlots)) return null;
  return entry.assignSlots;
};

// Convenio (PersonAssignment) vigente en una fecha. Devuelve la entrada
// completa con sus campos joineados (assignAgreementName, assignMonthlyHours,
// assignVacationDays, assignHolidayType).
export const agreementAtDate = (assignmentHistory, date, timeZone = DEFAULT_TIME_ZONE) => {
  return findActiveEntry(assignmentHistory, date, timeZone);
};

// Convierte CUALQUIER input que represente "el horario semanal" en una
// función uniforme `(isoDate) => slotOrNull`. Permite que las funciones de
// cómputo acepten indistintamente:
//   - null / undefined  → siempre devuelve null (modo "sin info")
//   - Map<dow, slot>    → mismo horario para todo el histórico (LEGACY)
//   - Array de versiones (ScheduleVersion[]) → resuelve por fecha
//   - función           → se devuelve tal cual
//
// El `dow` de salida sigue la convención JS: 0=Domingo .. 6=Sábado.
// `timeZone` solo importa si el resolver recibe un instante en vez de una
// clave 'YYYY-MM-DD'.
export const makeSlotResolver = (input, timeZone = DEFAULT_TIME_ZONE) => {
  if (input == null) return () => null;
  if (typeof input === 'function') return input;
  if (Array.isArray(input)) {
    return (date) => {
      const iso = isoOf(date, timeZone);
      if (!iso) return null;
      const slots = slotsAtDate(input, iso, timeZone);
      if (!slots) return null;
      const dow = dowOfDayKey(iso);
      return slots.find((s) => Number(s.dayOfWeek) === dow) || null;
    };
  }
  // Map<dow, slot> o objeto indexado por dow
  return (date) => {
    const iso = isoOf(date, timeZone);
    if (!iso) return null;
    const dow = dowOfDayKey(iso);
    if (!Number.isFinite(dow)) return null;
    if (typeof input.get === 'function') return input.get(dow) || null;
    return input[dow] || null;
  };
};

// Versión "Map<dow, slot>" del horario vigente en una fecha — útil cuando
// se necesita el objeto Map completo (no un slot puntual) pero respetando
// el histórico. Si el histórico está vacío, devuelve un Map vacío y el
// caller cae al fallback.
export const buildWorkHoursByDowAtDate = (scheduleHistory, date, timeZone = DEFAULT_TIME_ZONE) => {
  const m = new Map();
  const slots = slotsAtDate(scheduleHistory, date, timeZone);
  if (!slots) return m;
  for (const s of slots) {
    if (s && s.dayOfWeek != null) m.set(Number(s.dayOfWeek), s);
  }
  return m;
};
